import React, { useState, useContext } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { withStyles } from '@material-ui/core/styles';
import Button from '@material-ui/core/Button';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import ProfileIcon from '@material-ui/icons/AccountCircle';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import { UserSessionContext } from 'components/UserSession';
import useLogout from 'hooks/useLogout';
import { selectLocale } from 'models/localization';
import { setOpen as setProfilePageOpen } from 'models/profileModal';

import Routes from 'Routes';
import UserAvatar from 'components/UserAvatar';
import { FETCH_ASSETS_URL } from 'appenv';
import findAvailableLocale from 'models/helpers/findAvailableLocale';
import useNameByLocale from 'hooks/useNameByLocale';
import useMyProfileTreatment from 'hooks/useMyProfileTreatment';
import IconLogout from '../icons/Logout';

const IconContact = `${FETCH_ASSETS_URL}/assets/contact.svg`;

const UserAccountContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding-left: 6px;
  max-width: 200px;
`;

const UserAccountButton = withStyles(() => ({
  label: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    textTransform: 'none',
  },
}))(Button);

const UserNameContainer = styled.span`
  font-style: normal;
  font-weight: bold;
  font-size: 16px;
  line-height: 22px;
  color: #484848;
  margin-right: 16px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const StyledBtn = styled.div`
  color: #484848;
  text-decoration: none;
  display: flex;
  align-items: center;
`;

const ProfileMenuItem = styled(MenuItem)`
  padding-left: 14px;
`;

const ContactText = styled.span`
  margin-left: 6px;
`;

const ProfileText = styled.span`
  margin-left: 8px;
`;

const LogoutText = styled.span`
  margin-left: 12px;
`;

const StyledContactIcon = styled.img`
  max-width: 24px;
`;

const UserAccountView = () => {
  const dispatch = useDispatch();
  const { canSee } = useContext(UserSessionContext);
  const { pageNames: { profile } } = useSelector((state) => state.eventConfigs);
  const [anchorEl, setAnchorEl] = useState(null);
  const { t } = useTranslation();
  const { onLogoutClick } = useLogout();
  const locale = useSelector(selectLocale);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const name = useNameByLocale();

  const openProfilePage = () => {
    dispatch(setProfilePageOpen(true));
  };

  const profilePageTitle = findAvailableLocale(profile || {}, locale) || t('pages.profile', 'Profile');
  const enableMyProfile = useMyProfileTreatment();

  return (
    <UserAccountContainer>
      <UserAccountButton onClick={handleClick}>
        <UserNameContainer>
          {t('greeting', { name })}
        </UserNameContainer>
        <UserAvatar name={name} />
      </UserAccountButton>
      <Menu
        disablePadding
        id="user-settings-menu"
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        getContentAnchorEl={null}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
      >
        {
          canSee('contacts') && (
            <MenuItem component={RouterLink} onClick={handleClose} to={Routes.contacts}>
              <StyledBtn>
                <StyledContactIcon alt="my contact" src={IconContact} />
                <ContactText>
                  {t('contact.my_contact')}
                </ContactText>
              </StyledBtn>
            </MenuItem>
          )
        }
        {enableMyProfile && (
          <ProfileMenuItem component={RouterLink} onClick={handleClose} to={Routes.profile}>
            <StyledBtn onClick={openProfilePage}>
              <ProfileIcon color="#484848" />
              <ProfileText>
                {profilePageTitle}
              </ProfileText>
            </StyledBtn>
          </ProfileMenuItem>
        )}
        <MenuItem onClick={onLogoutClick}>
          <StyledBtn>
            <IconLogout color="#484848" />
            <LogoutText>
              {t('logout')}
            </LogoutText>
          </StyledBtn>
        </MenuItem>
      </Menu>
    </UserAccountContainer>
  );
};

export default UserAccountView;
